import { Component } from '@angular/core';
import { OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CompanyInterface } from '@app/interface/company';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableModule } from '@angular/material/table';
import { SavedProductsApi } from '@app/services/savedproducts-api.service';
import {
  PaginatedResponse,
  RecursiveInterface,
} from '@app/services/base-authenticated.service';
import { NgForm } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
} from 'rxjs/operators';

import { EventEmitter, Input, Output } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import { SavedProductInterface } from '@app/interface/savedproduct';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-savedproduct-autocomplete',
  imports: [
    MatTableModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatIconModule,
  ],
  standalone: true,
  templateUrl: './savedproduct-autocomplete.component.html',
  styleUrl: './savedproduct-autocomplete.component.scss',
})
export class SavedproductAutocompleteComponent {
  @Input() productControl!: FormControl;
  @Input() filteredOptions!: Observable<string[]>;
  @Output() optionSelected = new EventEmitter<SavedProductInterface>();

  currentPage = 1;
  hasMore = true;
  isLoading = false;
  allResults: string[] = []; // Stores all loaded elements
  allSavedProducts: SavedProductInterface[] = []; // Stores full products objects

  constructor(
    private snackBar: MatSnackBar,
    private SavedProductService: SavedProductsApi,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.initializeAutocomplete();
  }

  private initializeAutocomplete(): void {
    this.filteredOptions = this.productControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) => {
        // Reset pagination when search term changes
        this.currentPage = 1;
        this.hasMore = true;
        this.allResults = [];
        return this.filterSavedProducts(value);
      }),
    );
  }

  private filterSavedProducts(searchTerm: string): Observable<string[]> {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (!searchTerm || searchTerm.length < 1) {
      // return of([]);
      searchTerm = ' ';
    }

    if (this.isLoading) return of(this.allResults);

    this.isLoading = true;

    return this.SavedProductService.getAllSavedProducts(organization_id, {
      name__icontains: searchTerm,
      page: this.currentPage,
      is_deleted: false,
    }).pipe(
      map((response: RecursiveInterface<SavedProductInterface>) => {
        this.isLoading = false;

        // Update pagination based on actual response structure
        this.hasMore = response.actual_page < response.tot_pages;

        // For first page, replace existing results
        if (this.currentPage === 1) {
          this.allResults = response.results.map((product) => product.name);
          this.allSavedProducts = response.results;
        } else {
          // For subsequent pages, append results
          const newNames = response.results.map((product) => product.name);
          this.allResults = [...this.allResults, ...newNames];
          this.allSavedProducts = [
            ...this.allSavedProducts,
            ...response.results,
          ];
        }

        return this.allResults;
      }),
      catchError((error: any) => {
        this.isLoading = false;
        console.error('Error searching products:', error);
        // this.showErrorMessage('Errore durante il caricamento.');
        this.allResults = ['Prestazione 1', 'Prestazione 2'];
        return of(this.allResults);
      }),
    );
  }

  loadMoreProducts() {
    if (this.hasMore && !this.isLoading) {
      this.currentPage++;
      this.filterSavedProducts(this.productControl.value).subscribe();
    }
  }

  onOptionSelected(event: MatOptionSelectionChange, productName: string): void {
    console.log('Option selected event:', event); // Debug
    if (event.isUserInput) {
      // Find the company in allCompanies by name
      const selectedSavedProduct = this.allSavedProducts.find(
        (c) => c.name === productName,
      );
      console.log('Found company:', selectedSavedProduct); // Debug
      if (selectedSavedProduct) {
        this.optionSelected.emit(selectedSavedProduct); // Emit full company
      }
    }
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
